// =============================================================================
// AGC: DROID FARM - Management Zones Panel
// AGC: Displays field management zones with NDVI ranges and recommendations
// =============================================================================

import React from 'react'
import { Layers, MapPin, Lightbulb } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import type { ManagementZone } from '../../../types'

interface ManagementZonesPanelProps {
  zones: ManagementZone[] | null
  onToggleZoneOverlay?: () => void
  showOverlay?: boolean
  isLoading?: boolean
}

/**
 * AGC: Get zone color as hex (for display)
 */
function getZoneColor(zone: ManagementZone): string {
  // AGC: Use zone.color if provided, otherwise generate based on ID
  if (zone.color) return zone.color
  
  const colors = ['#1e6e1e', '#32a032', '#d4d464', '#c8a064', '#8b5a2b']
  return colors[zone.id % colors.length]
}

/**
 * AGC: Get zone label based on NDVI range
 */
function getZoneLabel(zone: ManagementZone, t: (key: string) => string): string {
  if (zone.label) return zone.label
  
  // AGC: Handle zones that don't have ndviRange property
  const avgNdvi = zone.ndviRange 
    ? (zone.ndviRange.min + zone.ndviRange.max) / 2 
    : zone.meanNdvi || 0
  
  if (avgNdvi >= 0.7) return t('fields.monitoring.zoneHighVigor')
  if (avgNdvi >= 0.5) return t('fields.monitoring.zoneMediumVigor')
  if (avgNdvi >= 0.3) return t('fields.monitoring.zoneLowVigor')
  return t('fields.monitoring.zoneStressed')
}

export const ManagementZonesPanel: React.FC<ManagementZonesPanelProps> = ({
  zones,
  onToggleZoneOverlay,
  showOverlay = false,
  isLoading = false,
}) => {
  const { t } = useTranslation()

  // AGC: Loading state
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-20 bg-gray-100 dark:bg-gray-700 rounded" />
          <div className="h-20 bg-gray-100 dark:bg-gray-700 rounded" />
        </div>
      </div>
    )
  }

  // AGC: No zones state
  if (!zones || zones.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('fields.monitoring.managementZones')}
          </h4>
        </div>
        <div className="flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="text-center">
            <Layers className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('fields.monitoring.noZonesData')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // AGC: Sort zones by NDVI (high to low) - handle missing ndviRange
  const sortedZones = [...zones].sort((a, b) => {
    const aMax = a.ndviRange?.max ?? a.meanNdvi ?? 0
    const bMax = b.ndviRange?.max ?? b.meanNdvi ?? 0
    return bMax - aMax
  })

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-4">
      {/* AGC: Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('fields.monitoring.managementZones')}
        </h4>
        
        {onToggleZoneOverlay && (
          <button
            onClick={onToggleZoneOverlay}
            className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg transition-colors ${
              showOverlay
                ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <MapPin className="w-3 h-3" />
            {showOverlay ? t('fields.monitoring.hideOnMap') : t('fields.monitoring.showOnMap')}
          </button>
        )}
      </div>

      {/* AGC: Zones List */}
      <div className="space-y-3">
        {sortedZones.map((zone, index) => {
          const zoneColor = getZoneColor(zone)
          const zoneLabel = getZoneLabel(zone, t)
          
          return (
            <div
              key={zone.id ?? `zone-${index}`}
              className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
            >
              <div className="flex items-start gap-3">
                {/* AGC: Zone Color Indicator */}
                <div
                  className="w-6 h-6 rounded flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: zoneColor }}
                />

                <div className="flex-1 min-w-0">
                  {/* AGC: Zone Header */}
                  <div className="flex items-center justify-between mb-1">
                    <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                      {zoneLabel}
                    </h5>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {zone.percentage !== undefined && zone.percentage !== null 
                        ? `${zone.percentage.toFixed(1)}%` 
                        : '--'}
                    </span>
                  </div>

                  {/* AGC: Zone Stats */}
                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-2">
                    <span>
                      NDVI: {zone.ndviRange 
                        ? `${zone.ndviRange.min?.toFixed(2) ?? '--'} - ${zone.ndviRange.max?.toFixed(2) ?? '--'}`
                        : zone.meanNdvi?.toFixed(2) ?? 'N/A'}
                    </span>
                    {zone.area !== undefined && zone.area !== null && zone.area > 0 && (
                      <span>
                        {zone.area.toFixed(1)} ha
                      </span>
                    )}
                  </div>

                  {/* AGC: Area Progress Bar */}
                  <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min(100, zone.percentage ?? 0)}%`,
                        backgroundColor: zoneColor,
                      }}
                    />
                  </div>

                  {/* AGC: Recommendation */}
                  {zone.recommendation && (
                    <div className="flex items-start gap-1.5 mt-2">
                      <Lightbulb className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-gray-600 dark:text-gray-300">
                        {zone.recommendation}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* AGC: Summary */}
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('fields.monitoring.zonesSummary', { count: zones.length })}
        </p>
      </div>
    </div>
  )
}

export default ManagementZonesPanel

